// The venue's own mark: its logo (or the name's first letter), its name and
// its tagline, as the widget's header shows them. One block for the header
// (WidgetShell), the full-page checkout's top bar (CheckoutPage) and, on a
// phone, the drawer's top bar (CartPanel): each covers the header while it
// is open. The owner, 26 Sept: "Let's keep the Fijnland details at the top of
// the checkout on the full screen cart."
//
// The identity is the header's own (the active brand's, else the tenant's):
// WidgetShell works it out once and provides it here, so the checkout never
// re-derives it and a multi-brand tenant's checkout shows the brand the guest
// was booking with. Display only: the tenant's words are never rewritten.
import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type ElementType,
} from "react";
import { optimizedImageUrl } from "../lib/image";

export interface VenueIdentity {
  /** The active brand's name, else the tenant's. */
  title: string;
  /** The header's strap line, or null when there is none. */
  tagline: string | null;
  /** The active brand's logo, else the tenant's, or null (the initial shows). */
  logoSrc: string | null;
}

/** The header's identity, from WidgetShell. Null outside it (the page falls back to the tenant). */
export const VenueIdentityContext = createContext<VenueIdentity | null>(null);

export function useVenueIdentity(): VenueIdentity | null {
  return useContext(VenueIdentityContext);
}

/**
 * The header's identity, or outside WidgetShell the tenant's own name and
 * logo (no tagline): what a view that covers the header shows in its place.
 */
export function useVenueIdentityOr(tenant: { name: string; logo_url: string | null }): VenueIdentity {
  return useVenueIdentity() ?? { title: tenant.name, tagline: null, logoSrc: tenant.logo_url };
}

/**
 * True while `el`'s text fits on its one line (it never wraps: nowrap). Kept
 * live as the room changes (a turned phone, a text size). Strict: the text's
 * own width (a Range, to the fraction of a pixel) against the box's, so a
 * line even a pixel too wide is left out rather than shown cut short by its
 * ellipsis (scrollWidth and clientWidth round, and let ~1.5px through).
 */
function useFitsOneLine(on: boolean, text: string | null) {
  const ref = useRef<HTMLParagraphElement | null>(null);
  const [fits, setFits] = useState(true);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!on || !el) {
      setFits(true);
      return;
    }
    // Hidden, the line keeps its width in the column (only its height goes),
    // so the same measure says when there is room for it again.
    const range = document.createRange();
    const check = () => {
      range.selectNodeContents(el);
      setFits(range.getBoundingClientRect().width <= el.getBoundingClientRect().width + 0.01);
    };
    check();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    if (el.parentElement) ro.observe(el.parentElement);
    return () => ro.disconnect();
  }, [on, text]);
  return { ref, fits };
}

export function VenueMark({
  identity,
  titleAs = "h1",
  decorative = false,
  taglineOneLineOrNone = false,
}: {
  identity: VenueIdentity;
  /** The name's element: the header's h1; elsewhere plain text. */
  titleAs?: ElementType;
  /** The name is read beside it: the logo is not read out again. */
  decorative?: boolean;
  /** The tagline shows only while it fits its one line (else it is left out, not cut short). */
  taglineOneLineOrNone?: boolean;
}) {
  const { title, tagline, logoSrc } = identity;
  const Title = titleAs;
  const line = useFitsOneLine(taglineOneLineOrNone, tagline);
  return (
    <div className="vt-header-brand">
      <div className="vt-logo">
        {logoSrc ? (
          <img
            src={optimizedImageUrl(logoSrc, { width: 240 })}
            alt={decorative ? "" : title}
            decoding="async"
          />
        ) : (
          <span aria-hidden={decorative || undefined}>{title.charAt(0)}</span>
        )}
      </div>
      <div className="vt-header-text">
        <Title className="vt-header-title">{title}</Title>
        {tagline && (
          <p ref={line.ref} data-fits={line.fits ? undefined : "false"}>
            {tagline}
          </p>
        )}
      </div>
    </div>
  );
}
